import React from "react";
import { Box } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { Theme } from "../../../../providers/ThemeProvider";
import useTheme from "../../../../common/hooks/useTheme";
import { Walkthrough } from "./types";
import { formatDuration, getWalkthroughPreviewSrc } from "./utils";

type Props = {
  walkthrough: Walkthrough;
  onClick: (walkthrough: Walkthrough) => void;
};

/**
 * Reusable card rendered in the Walkthroughs grid. Shows the video thumbnail
 * with a duration badge, a category pill, and the walkthrough title. Clicking
 * anywhere on the card opens the detail screen.
 */
const WalkthroughCard = ({ walkthrough, onClick }: Props) => {
  const theme: Theme = useTheme();

  return (
    <Box
      sx={styles.card(theme)}
      onClick={() => onClick(walkthrough)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onClick(walkthrough);
      }}
    >
      <Box sx={styles.imageWrap}>
        <Box
          component="video"
          src={getWalkthroughPreviewSrc(walkthrough)}
          poster={walkthrough.thumbnail}
          // With a poster the card never needs a video frame, so don't pull
          // metadata off a ~100 MB master just to render a still. Falls back to
          // the auto first-frame preview when no poster is authored.
          preload={walkthrough.thumbnail ? "none" : "metadata"}
          muted
          playsInline
          sx={styles.image}
        />
        <Box sx={styles.durationBadge(theme)}>
          <AccessTimeIcon sx={styles.durationIcon(theme)} />
          <Box component="span" sx={styles.durationText(theme)}>
            {formatDuration(walkthrough.durationMinutes)}
          </Box>
        </Box>
      </Box>

      <Box sx={styles.body(theme)}>
        <Box sx={styles.categoryPill(theme)}>{walkthrough.category}</Box>
        <Box sx={styles.title(theme)}>{walkthrough.title}</Box>
      </Box>
    </Box>
  );
};

export default WalkthroughCard;

const styles = {
  card: (theme: Theme) => ({
    display: "flex",
    flexDirection: "column" as const,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    overflow: "hidden",
    cursor: "pointer",
    boxShadow: "var(--box-shadow-general)",
    transition: "transform 0.15s ease, box-shadow 0.15s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      // border: `1px solid ${theme.colors_ds.PRIMARY_ICON_MAIN}`,
    },
  }),
  imageWrap: {
    position: "relative" as const,
    width: "100%",
    height: "206px",
  },
  image: {
    width: "100%",
    height: "100%",
    objectFit: "cover" as const,
    display: "block",
    pointerEvents: "none" as const,
    backgroundColor: "#000",
  },
  durationBadge: (theme: Theme) => ({
    position: "absolute" as const,
    top: "16px",
    right: "16px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "4px 8px",
    borderRadius: "8px",
    backgroundColor: theme.colorTokens.N[80],
    boxShadow: "var(--box-shadow-general)",
  }),
  durationIcon: (theme: Theme) => ({
    fontSize: "16px",
    color: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
  }),
  durationText: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "20px",
    color: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
    whiteSpace: "nowrap" as const,
  }),
  body: (theme: Theme) => ({
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "flex-start",
    gap: "8px",
    padding: "16px",
    borderTop: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
  }),
  categoryPill: (theme: Theme) => ({
    backgroundColor: theme.colorTokens.P[10],
    color: theme.colors_ds.PRIMARY_TEXT_MAIN,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "1.6",
    padding: "4px 8px",
    borderRadius: "16px",
    whiteSpace: "nowrap" as const,
  }),
  title: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    lineHeight: "25px",
  }),
};
